import { fetchLanguageData, LocalizelyLanguage, downloadTranslations } from '../api/localizely';
import Logger from '../utils/logger';

interface StoredLanguageData {
  languages: LocalizelyLanguage[];
  defaultLanguage: string;
  translations: Record<string, any>;
}

interface LanguageInfo {
  code: string;
  name: string;
  flag: string;
  strings?: number;
  reviewed?: number;
  reviewedProgress?: number;
}

class LanguageService {
  private static readonly STORAGE_KEY = 'alea_language_data';
  private static readonly TRANSLATIONS_KEY = 'alea_translations_'; // Prefix for language-specific translations

  // Known language information - comprehensive list for international support
  private static readonly KNOWN_LANGUAGES: Record<string, Omit<LanguageInfo, 'code'>> = {
    // European Languages
    'de': { name: 'Deutsch', flag: '🇩🇪' },
    'en': { name: 'English', flag: '🇺🇸' },
    'fr': { name: 'Français', flag: '🇫🇷' },
    'es': { name: 'Español', flag: '🇪🇸' },
    'it': { name: 'Italiano', flag: '🇮🇹' },
    'pt': { name: 'Português', flag: '🇵🇹' },
    'nl': { name: 'Nederlands', flag: '🇳🇱' },
    'pl': { name: 'Polski', flag: '🇵🇱' },
    'ru': { name: 'Русский', flag: '🇷🇺' },
    'sv': { name: 'Svenska', flag: '🇸🇪' },
    'da': { name: 'Dansk', flag: '🇩🇰' },
    'no': { name: 'Norsk', flag: '🇳🇴' },
    'fi': { name: 'Suomi', flag: '🇫🇮' },
    'is': { name: 'Íslenska', flag: '🇮🇸' },
    'cs': { name: 'Čeština', flag: '🇨🇿' },
    'sk': { name: 'Slovenčina', flag: '🇸🇰' },
    'hu': { name: 'Magyar', flag: '🇭🇺' },
    'ro': { name: 'Română', flag: '🇷🇴' },
    'bg': { name: 'Български', flag: '🇧🇬' },
    'hr': { name: 'Hrvatski', flag: '🇭🇷' },
    'sr': { name: 'Српски', flag: '🇷🇸' },
    'sl': { name: 'Slovenščina', flag: '🇸🇮' },
    'lt': { name: 'Lietuvių', flag: '🇱🇹' },
    'lv': { name: 'Latviešu', flag: '🇱🇻' },
    'et': { name: 'Eesti', flag: '🇪🇪' },
    'el': { name: 'Ελληνικά', flag: '🇬🇷' },
    'tr': { name: 'Türkçe', flag: '🇹🇷' },
    'uk': { name: 'Українська', flag: '🇺🇦' },
  };

  /**
   * Generate language info for any language code
   */
  private static generateLanguageInfo(lang: LocalizelyLanguage): LanguageInfo {
    const knownInfo = LanguageService.KNOWN_LANGUAGES[lang.langCode];
    
    return {
      code: lang.langCode,
      name: knownInfo?.name || lang.langName || lang.langCode.toUpperCase(),
      flag: knownInfo?.flag || '🌐',
      strings: lang.strings,
      reviewed: lang.reviewed,
      reviewedProgress: lang.reviewedProgress
    };
  }

  /**
   * Get stored language data from localStorage (fallback only)
   */
  static getStoredLanguageData(): StoredLanguageData | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;
      
      return JSON.parse(stored);
    } catch (error) {
      Logger.warn('Failed to parse stored language data:', error);
      return null;
    }
  }

  /**
   * Store language data in localStorage (fallback only)
   */
  static storeLanguageData(data: StoredLanguageData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      Logger.log(`💾 Language data stored in localStorage (${data.languages.length} languages)`);
    } catch (error) {
      Logger.warn('Failed to store language data:', error);
    }
  }

  /**
   * Fetch fresh language data from Localizely API
   */
  static async fetchFreshLanguageData(
    projectId: string, 
    defaultLanguage: string = 'de'
  ): Promise<StoredLanguageData> {
    try {
      Logger.log('🌐 Fetching fresh language data from Localizely...');
      
      const languageData = await fetchLanguageData(projectId, defaultLanguage);
      
      const storedData: StoredLanguageData = {
        languages: languageData.languages,
        defaultLanguage: languageData.defaultLanguage,
        translations: languageData.translations
      };
      
      // Store in localStorage for fallback
      this.storeLanguageData(storedData);
      
      Logger.log(`✅ Fresh language data fetched (${languageData.languages.length} languages)`);
      return storedData;
    } catch (error) {
      Logger.error('Failed to fetch fresh language data:', error);
      throw error;
    }
  }

  /**
   * Get language data (always fresh from API due to no versioning in Localizely)
   */
  static async getLanguageData(
    projectId?: string, 
    defaultLanguage: string = 'de'
  ): Promise<{
    languages: LanguageInfo[];
    defaultLanguage: string;
    translations: Record<string, any>;
    fromCache: boolean;
  }> {
    // Always fetch fresh data due to no versioning in Localizely
    if (!projectId) {
      // Try to use cached data as fallback if no project ID
      const stored = this.getStoredLanguageData();
      if (stored) {
        Logger.log('📋 Using cached language data (no project ID)');
        return {
          languages: stored.languages.map(this.generateLanguageInfo),
          defaultLanguage: stored.defaultLanguage,
          translations: stored.translations,
          fromCache: true
        };
      }
      throw new Error('Project ID required to fetch fresh language data');
    }

    try {
      const freshData = await this.fetchFreshLanguageData(projectId, defaultLanguage);
      
      return {
        languages: freshData.languages.map(this.generateLanguageInfo),
        defaultLanguage: freshData.defaultLanguage,
        translations: freshData.translations,
        fromCache: false
      };
    } catch (error) {
      Logger.warn('Failed to fetch fresh data, trying cache as fallback...', error);
      
      // Use cache as fallback if API fails
      const stored = this.getStoredLanguageData();
      if (stored) {
        Logger.log('📋 Using cached language data as fallback');
        return {
          languages: stored.languages.map(this.generateLanguageInfo),
          defaultLanguage: stored.defaultLanguage,
          translations: stored.translations,
          fromCache: true
        };
      }
      
      throw error;
    }
  }

  /**
   * Clear stored language data
   */
  static clearStoredData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      Logger.log('🗑️ Cleared stored language data');
    } catch (error) {
      Logger.warn('Failed to clear stored language data:', error);
    }
  }

  /**
   * Get stored translations for a specific language (fallback only)
   */
  static getCachedTranslations(langCode: string): Record<string, any> | null {
    try {
      const key = `${this.TRANSLATIONS_KEY}${langCode}`;
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      
      return JSON.parse(stored);
    } catch (error) {
      Logger.warn(`Failed to get stored translations for ${langCode}:`, error);
      return null;
    }
  }

  /**
   * Store translations for a specific language (fallback only)
   */
  static storeCachedTranslations(langCode: string, translations: Record<string, any>): void {
    try {
      const key = `${this.TRANSLATIONS_KEY}${langCode}`;
      localStorage.setItem(key, JSON.stringify(translations));
      Logger.log(`💾 ${langCode} translations stored for fallback`);
    } catch (error) {
      Logger.warn(`Failed to store translations for ${langCode}:`, error);
    }
  }

  /**
   * Clear cached translations for a specific language
   */
  static clearTranslationCache(langCode: string): void {
    try {
      const key = `${this.TRANSLATIONS_KEY}${langCode}`;
      localStorage.removeItem(key);
      Logger.log(`🗑️ Cleared translation cache for ${langCode}`);
    } catch (error) {
      Logger.warn(`Failed to clear translation cache for ${langCode}:`, error);
    }
  }



  /**
   * Download fresh translations for a specific language
   */
  static async downloadFreshTranslations(
    langCode: string,
    projectId: string
  ): Promise<Record<string, any> | null> {
    try {
      Logger.log(`📥 Downloading fresh translations for ${langCode}...`);
      
      // Always download from API
      const translations = await downloadTranslations(projectId, langCode);
      
      // Store for fallback
      this.storeCachedTranslations(langCode, translations);
      
      Logger.log(`✅ Fresh ${langCode} translations downloaded`);
      return translations;
    } catch (error) {
      Logger.error(`❌ Failed to download fresh translations for ${langCode}:`, error);
      return null;
    }
  }

  /**
   * Clear all translation caches except the active language
   */
  static clearInactiveTranslationCaches(activeLanguage: string): void {
    try {
      const keys = Object.keys(localStorage);
      const activeKey = `${this.TRANSLATIONS_KEY}${activeLanguage}`;
      
      keys.forEach(key => {
        if (key.startsWith(this.TRANSLATIONS_KEY) && key !== activeKey) {
          localStorage.removeItem(key);
          Logger.log(`🗑️ Cleared inactive translation cache: ${key}`);
        }
      });
    } catch (error) {
      Logger.warn('Failed to clear inactive translation caches:', error);
    }
  }

  /**
   * Clear all translation caches
   */
  static clearAllTranslationCaches(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.TRANSLATIONS_KEY)) {
          localStorage.removeItem(key);
        }
      });
      Logger.log('🗑️ Cleared all translation caches');
    } catch (error) {
      Logger.warn('Failed to clear all translation caches:', error);
    }
  }

  /**
   * Get fallback languages for when API is unavailable
   */
  static getFallbackLanguages(): LanguageInfo[] {
    const fallbackCodes = ['de', 'en', 'fr', 'pl'];
    return fallbackCodes.map(code => ({
      code,
      name: LanguageService.KNOWN_LANGUAGES[code]?.name || code.toUpperCase(),
      flag: LanguageService.KNOWN_LANGUAGES[code]?.flag || '🌐'
    }));
  }
}

export default LanguageService;
export type { LanguageInfo, StoredLanguageData }; 